// src/AudioRecognition/dsp/yin.ts
export interface YinOptions {
  sampleRate: number; // 22050 or 44100
  threshold?: number; // ~0.1–0.15
  bufferSize?: number; // 2048 window
}

export const createYin = (opts: YinOptions) => {
  const sampleRate = opts.sampleRate;
  const bufSize = opts.bufferSize ?? 2048;
  const threshold = opts.threshold ?? 0.12;
  const half = bufSize >> 1;

  // Work buffers
  const yinBuf = new Float32Array(half);

  function difference(signal: Float32Array) {
    for (let tau = 0; tau < half; tau++) {
      let sum = 0;
      for (let i = 0; i < half; i++) {
        const d = signal[i] - signal[i + tau];
        sum += d * d;
      }
      yinBuf[tau] = sum;
    }
  }

  function cumulative() {
    yinBuf[0] = 1;
    let running = 0;
    for (let tau = 1; tau < half; tau++) {
      running += yinBuf[tau];
      yinBuf[tau] = (yinBuf[tau] * tau) / (running || 1);
    }
  }

  function absoluteThreshold(): number {
    for (let tau = 2; tau < half; tau++) {
      if (yinBuf[tau] < threshold) {
        while (tau + 1 < half && yinBuf[tau + 1] < yinBuf[tau]) tau++;
        return tau;
      }
    }
    return -1;
  }

  function parabolic(tau: number): number {
    const x0 = tau < 1 ? tau : tau - 1;
    const x2 = tau + 1 < half ? tau + 1 : tau;
    if (x0 === tau || x2 === tau) return tau;
    const s0 = yinBuf[x0],
      s1 = yinBuf[tau],
      s2 = yinBuf[x2];
    const denom = 2 * (2 * s1 - s2 - s0);
    if (!denom) return tau;
    return tau + (s2 - s0) / denom;
  }

  return {
    estimateHz(frame: Float32Array): number | null {
      if (frame.length < bufSize) return null;
      difference(frame);
      cumulative();
      const tau = absoluteThreshold();
      if (tau < 2) return null;
      const betterTau = parabolic(tau);
      const hz = sampleRate / betterTau;
      return hz > 20 && hz < 4200 ? hz : null; // piano range
    },
  };
};
